import type { BaseEntity } from '../../BaseEntity';
import type { Creative } from './Creative';
import type { Campaign } from './Campaign';
import type { Displays_LineItem, Displays_Screen } from '../Displays';

export type Schedule = BaseEntity & {
    campaign_id: number;
    name: string;
    template: boolean;
    is_default: boolean;
    disable_flag: boolean;
    // include
    schedulables?: ScheduleSchedulable[];

    // include
    rules?: ScheduleRuleGet[];
};
type ScheduleFile = {
    created_at: string;
    file_name: string;
    id: number;
    updated_at: string;
    schedule_id: number;
    data: { refresh: boolean; status: string; message: string };
};

type ScheduleWithFile = {
    is_file: true;
    scheduleFile: ScheduleFile;
};

type ScheduleWithoutFile = {
    is_file: false;
    scheduleFile?: never;
};

export type ScheduleGet = BaseEntity & {
    campaign_id: number;
    name: string;
    template: boolean;
    is_default: boolean;
    disable_flag: boolean;
    schedulables?: ScheduleSchedulable[];
    rules?: ScheduleRuleGet[];
} & (ScheduleWithFile | ScheduleWithoutFile);

export enum SchedulableProofOfPlay {
    Empty = '',
    Target = 'target',
    Impacts = 'impacts',
}

export enum SchedulableType {
    Campaign = 'Campaign',
    Creative = 'Creative',
    LineItem = 'LineItem',
    package = 'package',
    site_group = 'site_group',
    // Screen = 'Screen',

    // Screen has a custom morph class because of legacy Displays system, where it is called `Displays`
    // Once we migrate the data we'll need to change this back to `Screen`
    Screen = 'Display',
    // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values -- the same morph class as Screen
    Display = 'Display',
    // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values -- an alias of package (unused in src)
    digital = 'package',
}

export type ScheduleSchedulable =
    | SchedulableCampaign
    | SchedulableCreative
    | SchedulableLineItem
    | SchedulableScreen;

export type Schedulable = BaseEntity & {
    schedule_id: number;
    schedulable_id: string;
    schedulable_type: SchedulableType;
    campaign_id: number;
    proof_of_play?: SchedulableProofOfPlay;

    // decimal with 10 digits of precision
    sov?: string;

    // include
    schedule?: Schedule;
};

export type SchedulableCampaign = Schedulable & {
    schedulable_type: SchedulableType.Campaign;

    // include
    schedulable: Campaign;
};

export type SchedulableCreative = Schedulable & {
    schedulable_type: SchedulableType.Creative;

    // include
    schedulable: Creative;
};

export type SchedulableLineItem = Schedulable & {
    schedulable_type: SchedulableType.LineItem;

    // include
    schedulable: Displays_LineItem;
};

export type SchedulableScreen = Schedulable & {
    schedulable_type: SchedulableType.Screen;

    // include
    schedulable: Displays_Screen;
};

/**
 * A rule's half-hour slot, as the API names it: `Rule::$bitwise['hours_of_day']` keys
 * (`uniliveapi` `app/Models/Schedule/Rule.php`). Each is the range the slot covers, and the last
 * one ends at '24:00', not '00:00'.
 */
export type ScheduleRuleHourRange =
    | '00:00 - 00:30'
    | '00:30 - 01:00'
    | '01:00 - 01:30'
    | '01:30 - 02:00'
    | '02:00 - 02:30'
    | '02:30 - 03:00'
    | '03:00 - 03:30'
    | '03:30 - 04:00'
    | '04:00 - 04:30'
    | '04:30 - 05:00'
    | '05:00 - 05:30'
    | '05:30 - 06:00'
    | '06:00 - 06:30'
    | '06:30 - 07:00'
    | '07:00 - 07:30'
    | '07:30 - 08:00'
    | '08:00 - 08:30'
    | '08:30 - 09:00'
    | '09:00 - 09:30'
    | '09:30 - 10:00'
    | '10:00 - 10:30'
    | '10:30 - 11:00'
    | '11:00 - 11:30'
    | '11:30 - 12:00'
    | '12:00 - 12:30'
    | '12:30 - 13:00'
    | '13:00 - 13:30'
    | '13:30 - 14:00'
    | '14:00 - 14:30'
    | '14:30 - 15:00'
    | '15:00 - 15:30'
    | '15:30 - 16:00'
    | '16:00 - 16:30'
    | '16:30 - 17:00'
    | '17:00 - 17:30'
    | '17:30 - 18:00'
    | '18:00 - 18:30'
    | '18:30 - 19:00'
    | '19:00 - 19:30'
    | '19:30 - 20:00'
    | '20:00 - 20:30'
    | '20:30 - 21:00'
    | '21:00 - 21:30'
    | '21:30 - 22:00'
    | '22:00 - 22:30'
    | '22:30 - 23:00'
    | '23:00 - 23:30'
    | '23:30 - 24:00';

/** `Rule::$bitwise['days_of_week']` keys. */
export type ScheduleRuleDay =
    | 'monday'
    | 'tuesday'
    | 'wednesday'
    | 'thursday'
    | 'friday'
    | 'saturday'
    | 'sunday';

/** What the API accepts for `hours_of_day` (see `ScheduleRulePayload` in `Requests.ts`). */
export type ScheduleRuleHoursOfDay = Record<ScheduleRuleHourRange, boolean>;

/** What the API accepts for `days_of_week` (see `ScheduleRulePayload` in `Requests.ts`). */
export type ScheduleRuleDaysOfWeek = Record<ScheduleRuleDay, boolean>;

/**
 * A rule as the API sends it (`RuleTransformer`). The write shape is different: see
 * `ScheduleRulePayload` in `Requests.ts`.
 */
export type ScheduleRuleGet = BaseEntity & {
    schedule_id: number;

    // date string as `yyyy-MM-dd`
    start_date?: string;

    // date string as `yyyy-MM-dd`
    end_date?: string;

    // only the slots that are on, in calendar order ('00:00 - 00:30' first). Empty: none on.
    hours_of_day: ScheduleRuleHourRange[];

    // only the days that are on, Monday first. Empty: none on.
    days_of_week: ScheduleRuleDay[];

    // decimal with 10 digits of precision
    sov?: string;

    // include
    schedule?: Schedule;

    // decimal:8 cast, so it arrives as a string ('10.00000000') despite this type
    slot_length?: number;
    audience_version_id?: number | null;
};
